// src/pos-app/surfaces/cashier/lib/transactionFlow.js
//
// F12a-N Task 5 — Charge path (MONEY). Faithful, cut-and-paste extraction
// from the legacy page. The legacy page (frontend/src/pages/pos/
// CashierSalesPage.js) is UNTOUCHED and keeps its own inline copies of every
// function below — nothing here is imported back into it. Only mechanical
// changes were made converting each function's closure-captured React state
// into explicit parameters (bundled into a single `ctx` object where the
// legacy closure captured many variables at once) and converting relative
// imports for this file's new location.
//
// Source-range table (legacy CashierSalesPage.js, current line numbers):
//   createItemsWithAddons        <- 729-815   (originally verbatim; rewritten
//                                    by Nested Add-ons Task 8 to emit
//                                    line_id/parent_line_id/group_id instead
//                                    of consolidating rows; since MOVED OUT to
//                                    utils/transactionLines.js and re-exported
//                                    from here — see the note at the export)
//   linkInventoryToTransaction   <- 817-878   (verbatim; params replace closure)
//   buildTransaction             <- 1052-1095 (handleImmediateSaleCreation's txn
//                                    construction, i.e. its Steps 1-4; the
//                                    function's Step 5 — opening the payment
//                                    modal — is UI glue and stays in SellSurface)
//   dispatchKitchenTickets       <- 1296-1344 (processTransactionWithPayment's
//                                    "Step 2.5" tracking block, both branches;
//                                    the enclosing try/catch that legacy wraps
//                                    around this block at the call site is
//                                    hoisted INTO this function so it still
//                                    never throws — same fail-open behaviour)
//   printSaleReceipts            <- 1346-1378 (processTransactionWithPayment's
//                                    "Step 3-4" receipt-data prep + printReceipts
//                                    call, pulled into its own function; the
//                                    call site in processTransactionWithPayment
//                                    below invokes it at the exact same point)
//   processTransactionWithPayment<- 1231-1486 (verbatim step-by-step; the two
//                                    blocks above are extracted call-outs,
//                                    everything else — cash/card/split
//                                    branching, transaction create, daily
//                                    counter increment, mark-submitted,
//                                    cross-tab orderSubmitted dispatch, form
//                                    reset, error handling — is inline here in
//                                    the same order)
//   createTransactionFromOrder   <- NOT page-local: already a shared, pure
//                                    export of utils/OrderManagement.js (used
//                                    by the legacy page's pending-order-charge
//                                    flow at CashierSalesPage.js:3268). Nothing
//                                    to extract — re-exported here so Task 6
//                                    has a single import point alongside the
//                                    rest of the charge-path functions.
//
// Every setter/getter the legacy closure captured is passed explicitly via a
// `ctx` object (see each function's destructuring block) instead of being
// read off component state — this is the only structural change from the
// legacy source; the logic, branching, and step ordering are unchanged.
import { format } from 'date-fns';
import api, { transactionService, orderTrackingService } from '../../../../services/api';
import tenantStorage from '../../../../services/tenantStorage';
import { printReceipts } from '../../../../utils/PrintingUtils';
import { startTicketPrint } from '../../../../utils/ticketPrintDispatch';
import { localPrintWouldBeSilentlyDropped } from '../../../../utils/printRouting';
import { selectionProblemsFrom, serverMessageFrom } from '../../../../utils/apiErrors';
import { createItemsWithAddons } from '../../../../utils/transactionLines';
import { kickDrawerForSale } from '../../../../utils/drawerKick';
import host from '../../../../host';

export { createTransactionFromOrder } from '../../../../utils/OrderManagement';

// createItemsWithAddons used to be defined here. It now lives in
// utils/transactionLines.js because OrderManagement.createTransactionFromOrder
// (the pending-order charge path) needs the same builder, and importing this
// file from there would close a cycle with the re-export directly above.
// Re-exported so this module stays the single import point for the charge
// path — CashierSalesPage.js and the surface components import it from here.
export { createItemsWithAddons };

// ---------------------------------------------------------------------------
// buildTransaction — legacy handleImmediateSaleCreation, CashierSalesPage.js:
// 1052-1095, Steps 1-4 (Step 5 "open the payment modal" is SellSurface glue).
// Totals come in via `totals` (the caller's builder.memoizedTotals, itself
// utils/OrderManagement.calculateTotals output) — NEVER recomputed here.
// ---------------------------------------------------------------------------
export const buildTransaction = async ({
    selectedProducts,
    totals,
    selectedDiningOption,
    tableNumber,
    tenantId,
    notes,
    addServiceCharge,
    discountAmount,
    appliedPromo,
    promoDiscountAmount,
    businessDaySalesDate,
    cashierName,
}) => {
    // Step 1: totals already calculated by the caller (legacy 1055).
    const { subtotal, tax, total } = totals;

    // Step 2: Create transaction items with add-ons (legacy 1058).
    const items = createItemsWithAddons(selectedProducts);

    // Step 3: Get current sales date (legacy 1061).
    const currentSalesDate = (await tenantStorage.getState('salesDate')) || businessDaySalesDate;

    // Step 4: Create transaction object (legacy 1064-1085).
    return {
        transaction_id: '', // Will be generated by backend
        date: format(new Date(), 'yyyy-MM-dd'),
        time: format(new Date(), 'HH:mm:ss'),
        sales_date: currentSalesDate,
        staff_name: cashierName,
        payment_method: '', // Will be set when payment method is selected
        dining_option: selectedDiningOption,
        table_number: tableNumber || null, // Daily order number for no-table options
        service_charge: addServiceCharge,
        discount_amount: discountAmount,
        total_amount: subtotal,
        final_amount: Math.max(0, total - promoDiscountAmount),
        tax,
        notes,
        items,
        products: selectedProducts, // Keep reference for receipt printing
        tenant_id: tenantId,
        applied_promo_id: appliedPromo?.promotion?.promotion_id || null,
        promo_discount_amount: promoDiscountAmount,
    };
};

// ---------------------------------------------------------------------------
// linkInventoryToTransaction — legacy CashierSalesPage.js:817-878 (verbatim;
// `products`/`transactionId`/`currentConfirmOrder`/`selectedDiningOption`/
// `cashierName` replace the page's closure-captured state).
// ---------------------------------------------------------------------------
export const linkInventoryToTransaction = async ({
    products,
    transactionId,
    currentConfirmOrder,
    selectedDiningOption,
    cashierName,
}) => {
    try {
        // Update pending order transactions if this is a pending order
        if (currentConfirmOrder) {
            const tableKey = currentConfirmOrder;
            await api.post('/inventory/update-pending-order-transactions', {
                table_key: tableKey,
                transaction_id: transactionId
            });
        } else {
            // For immediate sales, create new inventory transaction records
            // Convert products to proper TransactionItem format
            const transactionItems = products.flatMap(product => {
                const items = [{
                    product_code: product.product_code,
                    product_name: product.name,
                    category: product.category,
                    quantity: product.quantity,
                    unit_price: parseFloat(product.selling_price),
                    total_amount: parseFloat(product.selling_price) * product.quantity,
                    notes: product.notes || ''
                }];

                // Add addon items if they exist
                if (product.add_ons && product.add_ons.length > 0) {
                    product.add_ons.forEach(addon => {
                        items.push({
                            product_code: addon.product_code || `ADD${String(addon.id).padStart(3, '0')}`,
                            product_name: `${addon.name} - Add on`,
                            category: product.category,
                            quantity: product.quantity,
                            unit_price: parseFloat(addon.price),
                            total_amount: parseFloat(addon.price) * product.quantity,
                            notes: `Add-on for: ${product.name}`
                        });
                    });
                }

                return items;
            });

            await api.post('/inventory/link-to-transaction', {
                transaction_id: transactionId,
                items: transactionItems,
                dining_option: selectedDiningOption,
                created_by: cashierName
            });
        }
    } catch (error) {
        console.error('Error linking inventory to transaction:', error);
        throw error;
    }
};

// ---------------------------------------------------------------------------
// dispatchKitchenTickets — legacy processTransactionWithPayment "Step 2.5",
// CashierSalesPage.js:1296-1344. Creates order-tracking records for an
// immediate sale (category-split, one record per printable category that has
// items — legacy has NO "OTHER ITEMS" catch-all for TRACKING, only for
// receipt PRINTING, which printSaleReceipts below delegates to the reused
// utils/PrintingUtils.printReceipts) or, for a pending-order charge
// (currentConfirmOrder set — Task 6), updates the order's existing tracking
// records with the new transaction_id. The try/catch legacy wraps around
// this block at its call site (CashierSalesPage.js:1341-1344) is hoisted
// into this function so it keeps the exact same fail-open behaviour
// (tracking failures never fail the transaction) without callers needing
// their own wrapper.
// ---------------------------------------------------------------------------
export const dispatchKitchenTickets = async ({
    currentConfirmOrder,
    selectedProducts,
    printableCategories,
    selectedDiningOption,
    cashierName,
    notes,
    transactionId,
    tableNumber,
    pendingOrders,
    tenantConfig,
}) => {
    try {
        if (!currentConfirmOrder) {
            // This is an immediate sale
            for (const category of printableCategories) {
                const categoryItems = selectedProducts.filter(p => p.category === category);
                if (categoryItems.length > 0) {
                    const trackingData = {
                        transaction_id: transactionId,
                        category: category,
                        printed_categories: true,
                        items: categoryItems.map(item => ({
                            name: item.name,
                            quantity: item.quantity,
                            notes: item.notes || null,
                            add_ons: item.add_ons || []
                        })),
                        table_info: {
                            dining_option: selectedDiningOption,
                            // The number the customer was given, so the kitchen
                            // ticket can be matched to the person waiting for it.
                            //
                            // Hardcoded `null` until now, which is why a takeaway
                            // BAR ORDER printed no identifier at all while the
                            // customer's own receipt for the same sale said
                            // "Order: #019" — the only way to pair a bag on the
                            // pass with a customer was to read the item list.
                            //
                            // One field carries both kinds, exactly as it does on
                            // the transaction the receipt is rendered from: a
                            // no-table option holds the daily counter ("#019",
                            // set by SellSurface's pill handler) and a table
                            // option holds the table ("4"). The renderers split
                            // them on the leading "#" — "Order: #019" for the
                            // first, "Table: 4" for the second — so dine-in is
                            // unchanged and gains no redundant Order line.
                            //
                            // Deliberately NOT the tracking row's `order_id`
                            // column: that holds the internal ORDERxxxxxx key,
                            // which matches no transaction and tells a chef
                            // nothing (the original bug, Notion item 23).
                            table_number: tableNumber || null,
                            staff_name: cashierName || 'Unknown'
                        },
                        total_items_count: categoryItems.reduce((sum, item) => sum + item.quantity, 0),
                        notes: notes || null
                    };

                    const created = await orderTrackingService.createOrderTracking(trackingData);
                    // Device Printer Config Task 8 (caller wiring) — a KDS ticket
                    // fires alongside the tracking record, entirely best-effort:
                    // printCategoryTicket never throws on its own, but this is
                    // wrapped too so a caller mock/edge case can't ever turn a
                    // print failure into a failed dispatch or a failed sale.
                    //
                    // Whole-branch review, finding 1(b) — STARTED, NOT AWAITED.
                    // This runs after payment has already succeeded, under
                    // setLoading(true), before the receipt print and before
                    // the confirm modal clears. Awaiting it put a network
                    // round trip per category, serially, on that path: a
                    // Kitchen+Bar sale during a backend cold start left the
                    // till on a spinner with no cancel, and a cashier
                    // re-tapping a sale that had in fact completed. The
                    // tracking record above is still created and still
                    // awaited — only the paper is detached — and the
                    // categories now overlap instead of queueing behind
                    // each other. Nothing downstream reads a print result.
                    if (created?.tracking_id) {
                        startTicketPrint(category, created.tracking_id, tenantConfig);
                    }
                }
            }
        } else {
            // This is a pending order - update existing tracking records with transaction_id
            const orderToSubmit = pendingOrders[currentConfirmOrder];
            if (orderToSubmit && orderToSubmit.order_id) {
                try {
                    // Use the correct API endpoint for updating tracking records
                    await api.put(`/orders/tracking/update-by-order/${orderToSubmit.order_id}?transaction_id=${transactionId}`);
                } catch (updateError) {
                    console.error('Failed to update tracking with transaction_id:', updateError);
                    // Don't fail the transaction if tracking update fails - log and continue
                }
            }
        }
    } catch (trackingError) {
        console.error('Error handling order tracking:', trackingError);
        // Don't fail the transaction if tracking fails
    }
};

// ---------------------------------------------------------------------------
// printSaleReceipts — legacy processTransactionWithPayment "Step 3-4",
// CashierSalesPage.js:1346-1378 (verbatim receipt-data + tenantInfo prep,
// then the printReceipts call — printingEnabled / method are respected
// entirely inside the reused utils/PrintingUtils.printReceipts, untouched).
//
// Task N round 3 (user-QC finding 3) — NATIVE-ONLY addition (legacy's own
// inline copy of this step, CashierSalesPage.js:1346-1378, is untouched):
// no host has a silent (driverless) print bridge — the only local path left
// is the browser's hidden-iframe `iframe.contentWindow.print()` dialog, used
// for any method that isn't 'network'. iOS WKWebView swallows that call with
// zero signal: no dialog, no receipt, no error — which is why this surface
// warns rather than attempting it.
// `localPrintWouldBeSilentlyDropped` mirrors PrintingUtils' OWN decision
// input (shouldPrintAtAll — method !== 'network', from utils/printRouting.js,
// the same pure module PrintingUtils itself reads) so this gate can never
// drift from what PrintingUtils would actually do. When it's true, the dead
// attempt is skipped and the cashier gets an explicit toast instead of
// silence — EVERY attempt, since they need to know each time nothing
// printed. Network method is unaffected (shouldPrintAtAll already excludes
// it — the backend enqueue/poll owns printing there, correctly silent from
// the POS's side).
// ---------------------------------------------------------------------------
export const printSaleReceipts = ({
    transactionWithPayment,
    selectedProducts,
    currentConfirmOrder,
    cashierName,
    paymentMethod,
    selectedDiningOption,
    tableNumber,
    appliedPromo,
    tenantConfig,
    brandName,
    currency,
    printableCategories,
    printingEnabled,
    receiptTranslationsEnabled,
    receiptTranslationLanguage,
    createReceiptTranslator,
    dynamicTranslations,
    translationsLoaded,
    t,
    setError,
}) => {
    if (localPrintWouldBeSilentlyDropped(tenantConfig?.printing?.method, host)) {
        if (typeof setError === 'function') {
            setError(t
                ? t('receipt_printing_requires_network', {}, 'Receipt printing on this device requires network printing — enable it in Portal Settings.')
                : 'Receipt printing on this device requires network printing — enable it in Portal Settings.');
        }
        return;
    }

    // Step 3: Prepare receipt data
    const receiptData = {
        date: format(new Date(), 'yyyy-MM-dd'),
        time: format(new Date(), 'HH:mm:ss'),
        cashier: cashierName,
        paymentMethod: paymentMethod,
        diningOption: selectedDiningOption,
        tableNumber: tableNumber,
        products: selectedProducts,
        subtotal: transactionWithPayment.total_amount,
        discount: transactionWithPayment.discount_amount,
        promoDiscount: transactionWithPayment.promo_discount_amount || 0,
        promoName: appliedPromo?.promotion?.name || '',
        serviceCharge: transactionWithPayment.service_charge ?
            (transactionWithPayment.total_amount * (tenantConfig?.service_charge_rate || 0.12)) : 0,
        tax: transactionWithPayment.tax || 0,
        total: transactionWithPayment.final_amount,
        notes: transactionWithPayment.notes
    };

    const tenantInfo = {
        brand_name: brandName,
        currency,
        printable_categories: printableCategories,
        printing: tenantConfig?.printing,
        receipt_header: tenantConfig?.receipt_header,
        receipt_footer: tenantConfig?.receipt_footer,
        receipt_logo: tenantConfig?.receipt_logo ? (tenantConfig.receipt_logo.startsWith('http') ? tenantConfig.receipt_logo : `${window.location.origin}/${tenantConfig.receipt_logo}`) : null,
        // Epson direct print (PrintingUtils.js) needs the real transaction id
        // to fetch its render-and-address payload from the backend.
        transaction_id: transactionWithPayment.transaction_id
    };

    // Step 4: Print receipts
    const receiptT = createReceiptTranslator(receiptTranslationLanguage);
    printReceipts(receiptData, selectedProducts, !!currentConfirmOrder, tenantInfo, printingEnabled, receiptTranslationsEnabled, receiptTranslationLanguage, receiptT, dynamicTranslations, translationsLoaded);

    // Fire-and-forget: the sale is already committed, and whether a drawer
    // opened must never decide whether a transaction succeeded. The server
    // decides if this sale qualifies at all — see services/drawer_policy.py
    // — so there is no policy check here to drift out of step with it.
    // The .catch is belt-and-braces, not a workaround for a known failure
    // mode: kickDrawerForSale's own body is entirely try/catch and should
    // never reject. But nothing at this call site enforces that contract —
    // a future edit to that function (a throw added outside its try, or the
    // function made non-async) would otherwise turn into an unhandled
    // promise rejection on a live till, which is a strictly worse outcome
    // than a drawer that silently fails to open.
    kickDrawerForSale(transactionWithPayment.transaction_id).catch(() => {});
};

// ---------------------------------------------------------------------------
// processTransactionWithPayment — legacy CashierSalesPage.js:1231-1486,
// verbatim step order. `ctx` bundles every value the legacy closure captured
// (state + setters); dispatchKitchenTickets/printSaleReceipts above are
// called at the exact points legacy inlines them (Steps 2.5 and 3-4).
// ---------------------------------------------------------------------------
export const processTransactionWithPayment = async (paymentMethod, ctx) => {
    const {
        t,
        pendingTransaction,
        tenantId,
        currentConfirmOrder,
        selectedDiningOption,
        tableNumber,
        businessDaySalesDate,
        selectedProducts,
        cashierName,
        printableCategories,
        notes,
        pendingOrders,
        pendingOrderIds,
        brandName,
        currency,
        tenantConfig,
        receiptTranslationLanguage,
        createReceiptTranslator,
        dynamicTranslations,
        translationsLoaded,
        printingEnabled,
        receiptTranslationsEnabled,
        appliedPromo,
        splitBillRef,
        setPaymentConfirmModalOpen,
        setSelectedModalPaymentMethod,
        setLoading,
        setError,
        setSuccess,
        setPendingTransaction,
        setPendingOrders,
        setPrintedOrders,
        setCurrentConfirmOrder,
        setSelectedProducts,
        setNotes,
        setDiscountType,
        setDiscountPercentage,
        setDiscountAmount,
        setPayableAmount,
        setSelectedCategory,
        setSelectedSubcategory,
        setSelectedDiningOption,
        setTableNumber,
        setTableNumberConfirmed,
        setAddServiceCharge,
        setAddTax,
        setDiscountMode,
        setMobileCartOpen = () => {}, // no mobile cart drawer on the native surface
        resetPromo,
        loadTransactions,
    } = ctx;

    if (!paymentMethod) {
        return null;
    }

    if (!pendingTransaction) {
        console.error('No pending transaction found');
        setError(t('no_transaction_to_process'));
        return null;
    }

    const transactionWithPayment = {
        ...pendingTransaction,
        payment_method: paymentMethod,
        tenant_id: tenantId
    };

    // Set payment amounts based on method
    if (paymentMethod === 'Cash') {
        transactionWithPayment.cash_amount = transactionWithPayment.final_amount;
        transactionWithPayment.card_amount = 0;
    } else if (paymentMethod === 'Card') {
        transactionWithPayment.cash_amount = 0;
        transactionWithPayment.card_amount = transactionWithPayment.final_amount;
    } else if (paymentMethod === 'Split Payment') {
        // Use the split functions from SplitBillManager ref - show split type selection first
        if (splitBillRef.current) {
            splitBillRef.current.showSplitTypeModal();
        }
        setPaymentConfirmModalOpen(false);
        setSelectedModalPaymentMethod('Split Payment');
        return null;
    } else {
        transactionWithPayment.cash_amount = 0;
        transactionWithPayment.card_amount = 0;
    }

    let transactionResult = null;
    let orderWasSubmitted = false;

    try {
        setLoading(true);
        transactionResult = await transactionService.createTransaction(transactionWithPayment);

        if (!transactionResult || !transactionResult.transaction_id) {
            throw new Error('Transaction creation failed - no transaction ID returned');
        }

        // transactionWithPayment was built from pendingTransaction, whose
        // transaction_id is always '' (generated backend-side). Merge the
        // real id back in now so anything reading transactionWithPayment
        // below — printSaleReceipts included — has it.
        transactionWithPayment.transaction_id = transactionResult.transaction_id;

        // Increment daily order counter for no-table dining options after successful transaction
        if (tableNumber && tableNumber.startsWith('#')) {
            const salesDate = await tenantStorage.getState('salesDate') || businessDaySalesDate;
            const counterKey = `daily_order_${selectedDiningOption}_${salesDate}`;
            const current = parseInt(await tenantStorage.getState(counterKey)) || 0;
            await tenantStorage.setState(counterKey, current + 1);
        }

        // Step 2: Create inventory transactions with transaction_id
        try {
            // Link existing inventory deductions to this transaction
            await linkInventoryToTransaction({
                products: selectedProducts,
                transactionId: transactionResult.transaction_id,
                currentConfirmOrder,
                selectedDiningOption,
                cashierName,
            });
        } catch (inventoryError) {
            console.error('Error linking inventory to transaction:', inventoryError);
            // Continue - transaction was successful
        }

        // NEW STEP 2.5: Update tracking records with transaction ID
        await dispatchKitchenTickets({
            currentConfirmOrder,
            selectedProducts,
            printableCategories,
            selectedDiningOption,
            cashierName,
            notes,
            transactionId: transactionResult.transaction_id,
            // Read AFTER the counter bump above, deliberately: the bump writes
            // the next value to tenantStorage and never touches `tableNumber`,
            // which still holds the number this sale was rung under and the
            // same one printSaleReceipts puts on the customer's copy below.
            tableNumber,
            pendingOrders,
            tenantConfig,
        });

        // Step 3-4: Prepare receipt data + print. A print failure must never
        // abort the post-sale bookkeeping below (payment already succeeded),
        // so it is isolated the same way inventory linking is above.
        try {
            printSaleReceipts({
                transactionWithPayment,
                selectedProducts,
                currentConfirmOrder,
                cashierName,
                paymentMethod,
                selectedDiningOption,
                tableNumber,
                appliedPromo,
                tenantConfig,
                brandName,
                currency,
                printableCategories,
                printingEnabled,
                receiptTranslationsEnabled,
                receiptTranslationLanguage,
                createReceiptTranslator,
                dynamicTranslations,
                translationsLoaded,
                t,
                setError,
            });
        } catch (printError) {
            console.error('Error printing sale receipts:', printError);
            // Continue - transaction was successful
        }

        // Step 5: Mark order as submitted ONLY after everything succeeds
        if (currentConfirmOrder && pendingOrders[currentConfirmOrder]) {
            // Double-check the order still exists and is unsubmitted
            const orderToSubmit = pendingOrders[currentConfirmOrder];
            if (orderToSubmit && !orderToSubmit.is_submitted) {
                const orderId = pendingOrderIds[currentConfirmOrder];

                try {
                    await tenantStorage.markOrderSubmitted(currentConfirmOrder, true, orderId);
                    orderWasSubmitted = true;

                    // Remove from local state only after successful submission
                    setPendingOrders(prev => {
                        const newOrders = { ...prev };
                        delete newOrders[currentConfirmOrder];
                        return newOrders;
                    });

                    setPrintedOrders(prev => {
                        const newPrintedOrders = { ...prev };
                        delete newPrintedOrders[currentConfirmOrder];
                        return newPrintedOrders;
                    });

                    setCurrentConfirmOrder(null);
                } catch (submissionError) {
                    console.error('Failed to mark order as submitted:', submissionError);
                    // Don't throw here - transaction was successful, just log the error
                    setError('Transaction successful but failed to update order status. Please check manually.');
                }
            } else {
                console.warn('Order not found or already submitted:', currentConfirmOrder);
            }
        }

        // Step 6: Close modals and reset state
        setPaymentConfirmModalOpen(false);
        setPendingTransaction(null);
        setSelectedModalPaymentMethod(null);

        // Step 7: Reload transactions and reset form
        await loadTransactions();

        if (currentConfirmOrder) {
            const order = pendingOrders[currentConfirmOrder];
            if (order?.products) {
                // Get unique categories from order products
                const orderCategories = [...new Set(order.products.map(p => p.category))];
                orderCategories.forEach(cat => {
                    window.dispatchEvent(new CustomEvent('orderSubmitted', {
                        detail: { category: cat }
                    }));
                });
            }
        }

        // Reset form state
        setSelectedProducts([]);
        setNotes('');
        setDiscountType('amount');
        setDiscountPercentage(0);
        setDiscountAmount(0);
        setPayableAmount(0);

        setSelectedCategory(null);
        setSelectedSubcategory(null);
        setSelectedDiningOption(null);
        setTableNumber('');
        setTableNumberConfirmed(false);
        setAddServiceCharge(false);
        setAddTax(false);
        setDiscountMode('direct');
        setMobileCartOpen(false);

        setSelectedModalPaymentMethod(null);
        resetPromo();

        setSuccess(t('sale_recorded_successfully'));
        setLoading(false);

        // Clear success message
        setTimeout(() => {
            setSuccess('');
        }, 3000);

        // The caller needs to know money was ACTUALLY taken, and needs the
        // real backend-minted id. SellSurface records a paid order for
        // hand-over off the back of this — firing that on a failure or on a
        // deferred split would mark an order paid with nothing collected.
        // Note transactionResult, NOT pendingTransaction: the latter's
        // transaction_id is always '' (see buildTransaction).
        return transactionResult;

    } catch (err) {
        console.error('Error in processTransactionWithPayment:', err);

        // If we managed to mark the order as submitted but something else failed,
        // we should NOT revert the submission (transaction was successful)
        const selectionProblems = selectionProblemsFrom(err);

        if (orderWasSubmitted && transactionResult) {
            setError(t('transaction_completed_refresh_error'));
        } else if (selectionProblems) {
            // The server rejected the modifier selection before writing
            // anything. Name the groups that still need answering — the
            // cashier can fix it in the cart in seconds. Anything vaguer is a
            // dead end at the till: the cart is kept below for exactly this
            // retry, but only if they know what to change.
            setError(`${t('selection_incomplete')} ${selectionProblems.join(' • ')}`);
        } else {
            // Complete failure - transaction may not have been created.
            // Prefer what the SERVER said: axios only ever fills err.message
            // with "Request failed with status code 400", which tells the
            // cashier nothing, while the body says what to do about it.
            setError(`${t('failed_to_record_sale')} ${serverMessageFrom(err) || err.message || t('unknown_error')}`);
        }

        setLoading(false);
        setPaymentConfirmModalOpen(false);
        setSelectedModalPaymentMethod(null);

        // If transaction failed completely, keep the pending transaction for retry
        // (don't clear pendingTransaction so user can retry)

        // Nothing was banked (or we cannot prove it was) — never let the
        // caller treat this as a completed sale.
        return null;
    }
};
